import { Form as ANTDForm, Checkbox, Input } from 'antd'
import { ReactComponent as AppleIcon } from 'assets/icons/apple.svg'
import { ReactComponent as GoogleIcon } from 'assets/icons/google.svg'
import VerificationCodeSentModal from 'components/business/Auth/Modals/VerificationCodeSentModal'
import { useAuth } from 'hooks/useAuth'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ROUTE_PATH } from 'routes'

import {
  AppleButton,
  ContentContainer,
  Description,
  Divider,
  DividerText,
  DividerWrapper,
  ErrorText,
  Footer,
  Form,
  FormItem,
  Heading,
  IconButton,
  Link,
  SubmitButton,
} from '../styled'

const { Password } = Input

function Registration() {
  const { signUp, signInWith } = useAuth()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false)
  const [isAppleLoading, setIsAppleLoading] = useState(false)
  const [isGoogleLoading, setIsGoogleLoading] = useState(false)
  const [error, setError] = useState('')
  const [isErrors, setIsErrors] = useState(false)
  const [isVerificationCodeModalOpen, setIsVerificationCodeModalOpen] = useState(false)

  const [form] = Form.useForm()
  const email = Form.useWatch('email', { form })
  const password = Form.useWatch('password', { form })
  const confirmPassword = Form.useWatch('confirm-password', { form })
  const agree = Form.useWatch('agree', { form })

  useEffect(() => {
    if (email && password && confirmPassword) {
      form
        .validateFields()
        .then(() => setIsErrors(false))
        .catch(() => {
          setIsErrors(true)
        })
    }
  }, [email, password, confirmPassword, form])

  const onSignUp = async () => {
    setIsLoading(true)
    setError('')
    try {
      await signUp(email, password)

      setIsVerificationCodeModalOpen(true)
    } catch (e) {
      const errorMessages: Record<string, string> = {
        UsernameExistsException: 'An account with this email already exists.',
        InvalidPasswordException: 'Password does not meet requirements. Use at least 8 characters.',
        InvalidParameterException: 'Please check your email and password are valid.',
        TooManyRequestsException: 'Too many attempts. Please wait a moment and try again.',
      }
      setError(errorMessages[e.code] || 'Something went wrong. Please try again.')
    }
    setIsLoading(false)
  }

  const onSignUpWithApple = async () => {
    setIsAppleLoading(true)
    setError('')
    try {
      await signInWith('Apple')
    } catch (e) {
      setError(e.message)
    }
    setIsAppleLoading(false)
  }

  const onSignUpWithGoogle = async () => {
    setIsGoogleLoading(true)
    setError('')
    try {
      await signInWith('Google')
    } catch (e) {
      setError(e.message)
    }
    setIsGoogleLoading(false)
  }

  const onReceiveVerificationCode = () => {
    navigate(ROUTE_PATH.VERIFY_EMAIL, { state: { email } })
  }

  const isDisabled = !email || !password || !confirmPassword || !agree || isErrors

  return (
    <>
      <div />
      <ContentContainer>
        <Heading>Register</Heading>
        <Description>Complete the details below to create an account</Description>
        <Form form={form} layout="vertical" hideRequiredMark onValuesChange={() => setError('')}>
          <FormItem
            name="email"
            label="Email"
            validateTrigger={form.getFieldError('email').length ? 'onChange' : 'onBlur'}
            required
            rules={[
              { required: true, message: '' },
              {
                type: 'email',
                message: 'Incorrect email',
              },
            ]}
          >
            <Input placeholder="Enter your email" type="email" status={error ? 'error' : undefined} />
          </FormItem>
          <FormItem
            name="password"
            label="Password"
            validateTrigger={form.getFieldError('password').length ? 'onChange' : 'onBlur'}
            required
            rules={[
              {
                required: true,
                message: '',
              },
              {
                min: 8,
                message: 'Password must be at least 8 characters long',
              },
              {
                max: 20,
                message: 'Password must be at most 20 characters long',
              },
            ]}
          >
            <Password
              placeholder="Create password (at least 8 characters)"
              type="password"
              status={error ? 'error' : undefined}
            />
          </FormItem>
          <FormItem
            name="confirm-password"
            label="Confirm password"
            required
            rules={[
              {
                validator: (_, value) => {
                  if (!value || form.getFieldValue('password') === value) {
                    return Promise.resolve()
                  }
                  return Promise.reject(new Error('The two passwords that you entered do not match!'))
                },
              },
              {
                required: true,
                message: '',
              },
            ]}
          >
            <Password placeholder="Confirm your password" type="password" status={error ? 'error' : undefined} />
          </FormItem>
          <FormItem name="agree" valuePropName="checked">
            <Checkbox>
              I agree with{' '}
              <Link to={ROUTE_PATH.TERMS_OF_USE} target="_blank">
                Terms and Conditions
              </Link>{' '}
              and{' '}
              <Link to={ROUTE_PATH.PRIVACY_POLICY} target="_blank">
                Privacy Policy
              </Link>
            </Checkbox>
          </FormItem>
          {error && <ErrorText>{error}</ErrorText>}
          <ANTDForm.Item noStyle>
            <SubmitButton htmlType="submit" type="primary" disabled={isDisabled} loading={isLoading} onClick={onSignUp}>
              REGISTER
            </SubmitButton>
          </ANTDForm.Item>
          <DividerWrapper>
            <Divider />
            <DividerText>or</DividerText>
            <Divider />
          </DividerWrapper>
          <AppleButton type="primary" icon={<AppleIcon />} loading={isAppleLoading} onClick={onSignUpWithApple}>
            REGISTER WITH APPLE
          </AppleButton>
          <IconButton icon={<GoogleIcon />} loading={isGoogleLoading} onClick={onSignUpWithGoogle}>
            REGISTER WITH GOOGLE
          </IconButton>
        </Form>
      </ContentContainer>
      <Footer>
        Already have an account? <Link to={ROUTE_PATH.LOGIN}>Log in</Link>
      </Footer>
      <VerificationCodeSentModal open={isVerificationCodeModalOpen} onComplete={onReceiveVerificationCode} />
    </>
  )
}

export default Registration
